import React, { useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { Bulletin } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNADivider, DNAModal, DNAText, Modal } from '@alucio/lux-ui';
import { TitleBar } from 'src/components/DNA';
import BulletinEmptyStateImage from 'src/assets/images/BulletinEmptyState';
import { useDispatch } from 'src/state/redux';
import { bulletinActions } from 'src/state/redux/slice/bulletin';
import { BulletinEditor } from './BulletinEditor';
import { BulletinPost } from './BulletinPost';
import { useBulletinContext } from './BulletinProvider';
import { withdrawBulletin } from './bulletin.service';
import { useBulletins } from './useBulletins';

export const BulletinHistory: React.FC = () => {
  const { bulletins, drafts, canManage, user, name, hydrated } = useBulletins();
  const posts = canManage ? [...drafts, ...bulletins] : bulletins;
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const { setManagementDialogOpen } = useBulletinContext();
  const [editor, setEditor] = useState<Bulletin | 'new'>();
  const [withdraw, setWithdraw] = useState<Bulletin>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => () => {
    setManagementDialogOpen(false);
  }, [setManagementDialogOpen]);
  const openEditor = (post: Bulletin | 'new') => {
    setManagementDialogOpen(true);
    setEditor(post);
  };
  const closeEditor = () => {
    setEditor(undefined);
    setManagementDialogOpen(false);
  };
  const closeWithdrawal = () => {
    setWithdraw(undefined);
    setManagementDialogOpen(false);
  };
  const confirmWithdrawal = async () => {
    if (!withdraw || !user || busy) return;
    setBusy(true);
    setError('');
    try {
      dispatch(bulletinActions.upsert(await withdrawBulletin(withdraw.id, user)));
      closeWithdrawal();
    } catch {
      setError('The post could not be deleted. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const createButton = canManage && (
    <DNAButton onPress={() => openEditor('new')} testID="bulletin-create">
      Create post
    </DNAButton>
  );
  return (
    <DNABox appearance="col">
      <TitleBar title={name}>
        {canManage && <TitleBar.Button>{createButton}</TitleBar.Button>}
      </TitleBar>
      {!hydrated ? (
        <DNAText status="tertiary">Loading posts…</DNAText>
      ) : !posts.length ? (
        <DNABox
          appearance="col"
          alignX="center"
          alignY="center"
          style={{ minHeight: 520 }}
        >
          <DNABox
            appearance="col"
            spacing="sm"
            alignX="center"
            style={{ width: 360, maxWidth: '100%', padding: 16 }}
          >
            <BulletinEmptyStateImage />
            <DNABox appearance="col" spacing="md" alignX="center">
              <DNAText bold>No posts yet</DNAText>
              <DNAText status="tertiary" style={{ textAlign: 'center' }}>
                {canManage
                  ? 'Share team updates, deadlines, resources here.'
                  : 'Team updates will appear here once published.'}
              </DNAText>
              {canManage && <DNAButton onPress={() => openEditor('new')}>Create post</DNAButton>}
            </DNABox>
          </DNABox>
        </DNABox>
      ) : (
        <DNABox
          appearance="col"
          spacing="lg"
          style={{ width: '100%', paddingRight: 8, paddingBottom: 40 }}
        >
          {posts.map((post, index) => (
            <DNABox key={post.id} appearance="col" spacing="md">
              {index > 0 && <DNADivider />}
              <BulletinPost
                bulletin={post}
                onEdit={canManage ? () => openEditor(post) : undefined}
                onWithdraw={
                  canManage
                    ? () => {
                      setManagementDialogOpen(true);
                      setWithdraw(post);
                      setError('');
                    }
                    : undefined
                }
              />
            </DNABox>
          ))}
        </DNABox>
      )}
      {editor && canManage && user && (
        <BulletinEditor
          user={user}
          bulletin={editor === 'new' ? undefined : editor}
          onClose={closeEditor}
        />
      )}
      {withdraw && canManage && (
        <Modal visible backdropStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
          <DNAModal style={{ width: Math.min(480, width - 32), paddingVertical: 16 }}>
            <DNAModal.Header onClose={closeWithdrawal} disabled={busy}>
              Delete post
            </DNAModal.Header>
            <DNABox appearance="col" spacing="md" style={{ padding: 16 }}>
              <DNAText>
                Delete “{withdraw.title}”? It will no longer appear for your team.
              </DNAText>
              {!!error && (
                <DNAText status="danger" accessibilityRole="alert">
                  {error}
                </DNAText>
              )}
            </DNABox>
            <DNAModal.Confirmation>
              <DNAButton
                appearance="outline"
                disabled={busy}
                onPress={closeWithdrawal}
              >
                Cancel
              </DNAButton>
              <DNAButton status="danger" disabled={busy} onPress={confirmWithdrawal}>
                {busy ? 'Deleting…' : 'Delete'}
              </DNAButton>
            </DNAModal.Confirmation>
          </DNAModal>
        </Modal>
      )}
    </DNABox>
  );
};

export default BulletinHistory;
